export const TOOLTIP_DELAY = 120
let warmUntil = 0

export const tooltipDelay = (now: number) => now < warmUntil ? 0 : TOOLTIP_DELAY
export const warmTooltip = (now: number) => { warmUntil = now + 400 }

export interface TooltipTarget {
  element: HTMLElement
  content: string
}

/** Ближайшая подпись побеждает родительскую. У явного Tooltip свой обработчик. */
export function tooltipTarget(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element) || target.closest('[data-tooltip-owned]')) return null
  const element = target.closest<HTMLElement>('[data-tooltip]')
  return element?.dataset.tooltip?.trim() ? element : null
}

/** Одна подсказка на документ: без обёрток, дополнительных Tab и слушателей на каждой кнопке. */
export function installTooltips(doc: Document, change: (target: TooltipTarget | null) => void): () => void {
  const win = doc.defaultView!
  let hovered: HTMLElement | null = null
  let focused: HTMLElement | null = null
  let dismissed: HTMLElement | null = null
  let pending: HTMLElement | null = null
  let active: HTMLElement | null = null
  let timer: number | undefined

  const close = () => {
    win.clearTimeout(timer)
    timer = undefined
    pending = null
    observer.disconnect()
    if (active) {
      warmTooltip(win.performance.now())
      active = null
      change(null)
    }
  }
  const observer = new MutationObserver(() => {
    const element = active ?? pending
    if (!element) return
    if (!element.isConnected || !element.dataset.tooltip?.trim()) {
      if (hovered === element) hovered = null
      if (focused === element) focused = null
      close()
    } else if (active) {
      change({ element, content: element.dataset.tooltip })
    }
  })
  const show = (element: HTMLElement | null) => {
    if (element === dismissed) element = null
    if (element && (element === pending || element === active)) return
    close()
    if (!element) return
    pending = element
    // Данные меняются после действий, а кнопка может исчезнуть при смене экрана.
    observer.observe(element, { attributes: true, attributeFilter: ['data-tooltip'] })
    observer.observe(doc.body, { childList: true, subtree: true })
    timer = win.setTimeout(() => {
      pending = null
      if (!element.isConnected || !element.dataset.tooltip?.trim()) { close(); return }
      active = element
      change({ element, content: element.dataset.tooltip })
    }, tooltipDelay(win.performance.now()))
  }
  const enter = (event: PointerEvent) => {
    if (event.pointerType === 'touch') return
    if (event.target instanceof Element && event.target.closest('[data-tooltip-owned]')) {
      hovered = null
      close()
      return
    }
    const element = tooltipTarget(event.target)
    if (element !== hovered) {
      hovered = element
      if (element !== dismissed) dismissed = null
      show(hovered ?? focused)
    }
  }
  const leave = (event: PointerEvent) => {
    if (event.pointerType === 'touch') return
    const next = tooltipTarget(event.relatedTarget)
    if (next !== hovered) {
      hovered = next
      if (next !== dismissed) dismissed = null
      show(hovered ?? focused)
    }
  }
  const focus = (event: FocusEvent) => {
    if (event.target instanceof Element && event.target.closest('[data-tooltip-owned]')) {
      focused = null
      close()
      return
    }
    const element = tooltipTarget(event.target)
    focused = element?.matches(':focus-visible') ? element : null
    if (focused) dismissed = null
    show(focused ?? hovered)
  }
  const blur = (event: FocusEvent) => {
    if (tooltipTarget(event.target) !== focused) return
    focused = null
    show(hovered)
  }
  const dismiss = () => {
    dismissed = active ?? pending ?? hovered ?? focused
    close()
  }
  const key = (event: KeyboardEvent) => {
    if (['Escape', 'Enter', ' '].includes(event.key)) { dismiss(); return }
    // Клавиатурой можно продолжить после клика: фокус не меняется, но становится видимым.
    const element = tooltipTarget(event.target)
    if (element?.matches(':focus-visible') && element !== focused) {
      focused = element
      dismissed = null
      show(focused)
    }
  }
  const out = () => { hovered = null; focused = null; dismiss() }
  doc.addEventListener('pointerover', enter)
  doc.addEventListener('pointerout', leave)
  doc.addEventListener('focusin', focus)
  doc.addEventListener('focusout', blur)
  doc.addEventListener('pointerdown', dismiss, true)
  doc.addEventListener('keydown', key, true)
  doc.addEventListener('scroll', dismiss, true)
  doc.addEventListener('fullscreenchange', dismiss)
  win.addEventListener('resize', dismiss)
  win.addEventListener('blur', out)
  return () => {
    close()
    doc.removeEventListener('pointerover', enter)
    doc.removeEventListener('pointerout', leave)
    doc.removeEventListener('focusin', focus)
    doc.removeEventListener('focusout', blur)
    doc.removeEventListener('pointerdown', dismiss, true)
    doc.removeEventListener('keydown', key, true)
    doc.removeEventListener('scroll', dismiss, true)
    doc.removeEventListener('fullscreenchange', dismiss)
    win.removeEventListener('resize', dismiss)
    win.removeEventListener('blur', out)
  }
}
